import {DOCUMENT} from '@angular/common';
import {inject, Injectable, NgZone} from '@angular/core';

declare global {
    interface Window {
        hbspt?: {
            forms?: {
                create: (options: Record<string, unknown>) => void;
            };
        };
        __karma__?: unknown;
    }
}

@Injectable({providedIn: 'root'})
export class HubspotFormsService {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly ngZone = inject(NgZone);

    private scriptLoader: Promise<void> | null = null;
    private scriptLoaded = false;

    ensureEmbedScript(region: string): Promise<void> {
        const win = this.document.defaultView;

        if (!win || typeof window === 'undefined' || (win as Window).__karma__) {
            return Promise.resolve();
        }

        if (this.scriptLoaded || win.hbspt?.forms?.create) {
            this.scriptLoaded = true;
            return Promise.resolve();
        }

        if (this.scriptLoader) {
            return this.scriptLoader;
        }

        const src = `https://js-${region}.hsforms.net/forms/embed/v2.js`;

        this.scriptLoader = this.ngZone.runOutsideAngular(
            () =>
                new Promise<void>((resolve, reject) => {
                    const existing = this.document.getElementById('hubspot-forms-embed-script') as HTMLScriptElement | null;
                    if (existing) {
                        if (existing.dataset['loaded'] === 'true') {
                            this.scriptLoaded = true;
                            resolve();
                            return;
                        }

                        existing.addEventListener(
                            'load',
                            () => {
                                this.scriptLoaded = true;
                                existing.dataset['loaded'] = 'true';
                                resolve();
                            },
                            {once: true},
                        );
                        existing.addEventListener('error', reject, {once: true});
                        return;
                    }

                    const script = this.document.createElement('script');
                    script.id = 'hubspot-forms-embed-script';
                    script.type = 'text/javascript';
                    script.src = src;
                    script.async = true;
                    script.defer = true;
                    script.charset = 'utf-8';
                    script.onload = () => {
                        this.scriptLoaded = true;
                        script.dataset['loaded'] = 'true';
                        resolve();
                    };
                    script.onerror = (err) => reject(err);
                    this.document.head.appendChild(script);
                }),
        );

        return this.scriptLoader;
    }
}

